import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/hooks/useCart";

export default function CartSidebar() {
  const { items, isOpen, setIsOpen, removeItem, updateQuantity, total, itemCount } = useCart();
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimating(true));
      });
      document.body.style.overflow = "hidden";
    } else {
      setAnimating(false);
      const timer = setTimeout(() => setVisible(false), 350);
      document.body.style.overflow = "";
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-midnight/40 backdrop-blur-sm"
        style={{
          opacity: animating ? 1 : 0,
          transition: "opacity 350ms ease",
        }}
        onClick={() => setIsOpen(false)}
      />

      {/* Sidebar Panel */}
      <div
        className="fixed top-0 right-0 h-full z-50 w-full sm:w-[420px] bg-moonlight flex flex-col"
        style={{
          fontFamily: "'Jost', sans-serif",
          transform: animating ? "translateX(0)" : "translateX(100%)",
          transition: "transform 350ms cubic-bezier(0.32, 0, 0.15, 1)",
          boxShadow: "-8px 0 40px rgba(0,0,0,0.12)",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-pearl/40">
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.4rem", color: "#1A2A32", letterSpacing: "0.05em" }}>
            Your Cart
          </h2>
          <button
            onClick={() => setIsOpen(false)}
            className="w-8 h-8 flex items-center justify-center text-midnight/60 hover:text-midnight transition-colors cursor-pointer"
            aria-label="Close cart"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Shipping Banner */}
        {total < 80 && (
          <div className="px-6 py-3 bg-moon-gold/20 text-center">
            <p className="text-xs text-midnight/70 tracking-wide">
              Add <strong>${(80 - total).toFixed(2)}</strong> more for free shipping on US orders
            </p>
          </div>
        )}
        {total >= 80 && (
          <div className="px-6 py-3 bg-midnight/5 text-center">
            <p className="text-xs text-midnight/70 tracking-wide">
              🎉 You qualify for <strong>free shipping</strong>!
            </p>
          </div>
        )}

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-pearl/20">
                <i className="ri-shopping-bag-line text-2xl text-midnight/30" />
              </div>
              <div>
                <p className="text-sm text-midnight/50">Your cart is empty</p>
                <p className="text-xs text-midnight/30 mt-1">Add something luminous to begin</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="mt-2 text-xs tracking-widest uppercase text-midnight border-b border-midnight/30 pb-0.5 hover:border-midnight transition-colors cursor-pointer whitespace-nowrap"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {items.map((item) => (
                <div key={item.cartId} className="flex gap-4">
                  {/* Image */}
                  <div className="w-20 h-24 shrink-0 rounded overflow-hidden bg-pearl/20">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2">
                      <p className="text-sm text-midnight font-medium leading-tight line-clamp-2">{item.name}</p>
                      <button
                        onClick={() => removeItem(item.cartId)}
                        className="shrink-0 w-5 h-5 flex items-center justify-center text-midnight/30 hover:text-midnight transition-colors cursor-pointer"
                        aria-label="Remove item"
                      >
                        <i className="ri-close-line text-sm" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-midnight/40">{item.size}</span>
                      <span className="text-midnight/20">·</span>
                      <span className="flex items-center gap-1 text-xs text-midnight/40">
                        <span
                          className="w-3 h-3 rounded-full border border-pearl/50 inline-block"
                          style={{ backgroundColor: item.colorHex }}
                        />
                        {item.color}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      {/* Quantity */}
                      <div className="flex items-center gap-2 border border-pearl rounded">
                        <button
                          onClick={() => updateQuantity(item.cartId, item.quantity - 1)}
                          className="w-7 h-7 flex items-center justify-center text-midnight/60 hover:text-midnight transition-colors cursor-pointer"
                        >
                          <i className="ri-subtract-line text-xs" />
                        </button>
                        <span className="text-xs text-midnight min-w-[16px] text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.cartId, item.quantity + 1)}
                          className="w-7 h-7 flex items-center justify-center text-midnight/60 hover:text-midnight transition-colors cursor-pointer"
                        >
                          <i className="ri-add-line text-xs" />
                        </button>
                      </div>
                      <p className="text-sm font-medium text-midnight">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-6 py-5 border-t border-pearl/40 space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-midnight/60">Subtotal ({itemCount} item{itemCount !== 1 ? "s" : ""})</span>
              <span className="text-base font-medium text-midnight">${total.toFixed(2)}</span>
            </div>
            <p className="text-xs text-midnight/40">Taxes and shipping calculated at checkout.</p>
            <Link
              to="/checkout"
              onClick={() => setIsOpen(false)}
              className="w-full block text-center py-3.5 bg-midnight text-moonlight text-xs tracking-widest uppercase hover:bg-midnight-dark transition-colors duration-300 cursor-pointer whitespace-nowrap rounded"
            >
              Proceed to Checkout
            </Link>
            <button
              onClick={() => setIsOpen(false)}
              className="w-full text-center text-xs tracking-wider uppercase text-midnight/50 hover:text-midnight transition-colors cursor-pointer whitespace-nowrap"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
}
